import { componentInterface } from '@ardoq/component-interface';
import { componentOperations } from '@ardoq/core';
import { sanitizeFilterColorClassName } from '@ardoq/color-helpers';
import { APIComponentAttributes } from '@ardoq/api-types';
import { DiffType, GetCssClassNamesOption } from '@ardoq/data-model';
import { BaseNode } from './BaseNode';
import { GraphItemModel } from './GraphItem';
import type { Node } from './types';
import type { ComponentTypeInfo } from './componentTypeInfo';
import { filterCache } from 'collections/filterCache';

export class ComponentNode extends BaseNode<APIComponentAttributes> {
  private readonly typeInfo: ComponentTypeInfo;

  constructor(options: {
    dataModel: APIComponentAttributes;
    parent?: Node<GraphItemModel>;
    typeInfo: ComponentTypeInfo;
  }) {
    super(options);
    this.typeInfo = options.typeInfo;
  }

  getShape(): string {
    return (
      componentOperations.getShape(
        this.dataModel,
        this.typeInfo.componentType
      ) || 'predefinedProcess'
    );
  }

  getImage(): string | null {
    return componentOperations.getImage(
      this.dataModel,
      this.typeInfo.componentType
    );
  }

  getCSS(options?: GetCssClassNamesOption): string {
    return componentOperations.getCssClassNames(
      this.dataModel,
      this.typeInfo.componentType,
      this.getCSSFilterColor(),
      options
    );
  }

  getCSSFilterColor(): string | undefined {
    const color = filterCache.getColor(this.dataModel._id);
    return color ? sanitizeFilterColorClassName(color) : undefined;
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  isComponent(): this is ComponentNode {
    return true;
  }

  name(): string {
    return (
      componentInterface.getNameWithFieldLabelAndValue(this.dataModel._id) ?? ''
    );
  }

  isIncludedInContextByFilter(): boolean {
    return componentInterface.isIncludedInContextByFilter(this.dataModel);
  }

  isColoredByFilter(): boolean {
    return Boolean(filterCache.getColor(this.dataModel._id));
  }

  hasURLFields(): boolean {
    return componentOperations.hasURLFields(
      this.dataModel,
      this.typeInfo.urlFields
    );
  }

  getVisualDiffType = (): DiffType =>
    componentOperations.getVisualDiffType(this.dataModel);
}
